import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MinuteRequestsPanel — Oracle Portal section for the extra-minute
 * requests beta testers send from the dashboard usage meter. Admin
 * can approve (grants minutes into the user's current period + clears
 * any grace lock) or reject (optional note shown to the user).
 *
 * Follows the same visual pattern as SecurityEventsPanel for cohesion.
 */
const MinuteRequestsPanel = ({ adminSecret }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    if (!adminSecret) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/minute-requests`, {
        params: { admin_secret: adminSecret },
      });
      setRequests(data || []);
    } catch (err) {
      // Non-fatal — section stays empty, rest of the Oracle still works.
      if (process.env.NODE_ENV !== 'production') console.debug('minute requests fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [adminSecret]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const resolve = async (requestId, action) => {
    if (resolvingId) return;
    let adminNote = null;
    if (action === 'reject') {
      // Give the admin a quick way to tell the user why. Empty is fine.
      adminNote = window.prompt('Optional note to the user (shown in their email):', '');
      if (adminNote === null) return; // cancelled
    }
    setResolvingId(requestId);
    try {
      const url = `${API}/admin/minute-requests/${action}`;
      await axios.post(url, {
        request_id: requestId,
        admin_secret: adminSecret,
        admin_note: adminNote || undefined,
      });
      toast.success(
        action === 'approve'
          ? 'Approved — user will be notified by email'
          : 'Rejected — user will be notified by email',
      );
      fetchRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Failed to ${action}`;
      toast.error(typeof msg === 'string' ? msg : `Failed to ${action}`);
    } finally {
      setResolvingId(null);
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const resolved = requests.filter((r) => r.status !== 'pending').slice(0, 10);

  const formatWhen = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
  };

  return (
    <div className="mt-16" data-testid="minute-requests-panel">
      <div className="mb-6">
        <h2
          className="text-2xl font-light tracking-tight text-zinc-100"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          Minute Requests
        </h2>
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
          Beta testers asking for extra Auto Conversation time
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-zinc-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && pending.length === 0 && resolved.length === 0 && (
        <div className="bg-stone-950/50 border border-white/5 rounded-sm p-8 text-center text-zinc-500 text-sm">
          No minute requests yet.
        </div>
      )}

      {/* Pending — the actionable queue */}
      {pending.length > 0 && (
        <div className="bg-stone-950/50 border border-amber-400/20 rounded-sm overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-200/80">
            <Clock className="w-3 h-3" />
            Pending ({pending.length})
          </div>
          <ul>
            {pending.map((r) => (
              <li
                key={r.request_id}
                data-testid={`minute-request-${r.request_id}`}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-4 py-4 border-b border-white/5 last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">
                    {r.user_email}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    <strong className="text-amber-200/80">+{r.minutes_requested}m</strong>
                    <span className="text-zinc-600 mx-1.5">·</span>
                    requested {formatWhen(r.requested_at)}
                  </div>
                  {r.note && (
                    <div className="text-xs text-zinc-500 italic mt-1.5 line-clamp-3">
                      "{r.note}"
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => resolve(r.request_id, 'reject')}
                    disabled={resolvingId === r.request_id}
                    data-testid={`reject-${r.request_id}`}
                    className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border border-white/15 text-zinc-400 hover:text-white hover:border-white/30 disabled:opacity-60 transition-colors"
                  >
                    <XCircle className="w-3 h-3" />
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => resolve(r.request_id, 'approve')}
                    disabled={resolvingId === r.request_id}
                    data-testid={`approve-${r.request_id}`}
                    className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 hover:bg-emerald-500/30 disabled:opacity-60 transition-colors"
                  >
                    {resolvingId === r.request_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3 h-3" />
                    )}
                    Approve
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent resolved — audit tail */}
      {resolved.length > 0 && (
        <div className="bg-stone-950/40 border border-white/5 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 text-xs font-mono uppercase tracking-widest text-zinc-500">
            Recently resolved
          </div>
          <ul>
            {resolved.map((r) => (
              <li
                key={r.request_id}
                className="flex items-start justify-between gap-4 px-4 py-3 border-b border-white/5 last:border-0 text-xs text-zinc-400"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-zinc-200">{r.user_email}</span>
                  <span className="text-zinc-600 mx-1.5">·</span>
                  <span>+{r.minutes_requested}m</span>
                  {r.admin_note && (
                    <div className="text-zinc-500 italic mt-1">"{r.admin_note}"</div>
                  )}
                </div>
                <span
                  className={`shrink-0 text-[10px] font-mono uppercase tracking-widest ${
                    r.status === 'approved' ? 'text-emerald-300/80' : 'text-zinc-500'
                  }`}
                >
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MinuteRequestsPanel;
